import { TAGS, api } from "@/store/api"
import { patchUserWallets } from "../userAPI"
import type { IUser } from "../userAPI"
import type {
  ISendVerificationArgs,
  IVerifyWithdrawalArgs,
  IAttachWalletArgs,
  IDisconnectWalletArgs,
  IWithdrawalHistoryArgs,
  IWithdrawalHistoryResponse,
  ITransaction,
  IWithdrawalConfig,
  IWithdrawalCoin,
  IStarCourse
} from "./withdrawal.types"

const basePath = "/withdrawal"

export const withdrawalApi = api.enhanceEndpoints({
  addTagTypes: [TAGS.WITHDRAWAL, TAGS.BALANCE, TAGS.USER]
})

export const withdrawalEndpoints = withdrawalApi.injectEndpoints({
  endpoints: (builder) => ({
    sendVerification: builder.mutation<
      { success: true; message: string },
      ISendVerificationArgs
    >({
      query: (data) => ({
        url: `${basePath}/send-verification`,
        method: "POST",
        data
      })
    }),

    verifyWithdrawal: builder.mutation<ITransaction, IVerifyWithdrawalArgs>({
      query: (data) => ({
        url: `${basePath}/verify`,
        method: "POST",
        data
      }),
      invalidatesTags: [
        { type: TAGS.WITHDRAWAL, id: "HISTORY" },
        { type: TAGS.BALANCE },
        { type: TAGS.USER, id: "BALANCE" }
      ]
    }),

    cancelWithdrawal: builder.mutation<ITransaction, string>({
      query: (transactionId) => ({
        url: `${basePath}/cancel/${transactionId}`,
        method: "POST"
      }),
      invalidatesTags: [
        { type: TAGS.WITHDRAWAL, id: "HISTORY" },
        { type: TAGS.BALANCE },
        { type: TAGS.USER, id: "BALANCE" }
      ]
    }),

    getWithdrawalHistory: builder.query<
      IWithdrawalHistoryResponse,
      IWithdrawalHistoryArgs
    >({
      query: (params) => ({
        url: `${basePath}/history`,
        method: "GET",
        params
      }),
      providesTags: [{ type: TAGS.WITHDRAWAL, id: "HISTORY" }]
    }),

    getWithdrawalConfig: builder.query<IWithdrawalConfig, IWithdrawalCoin>({
      query: (coin) => ({
        url: `${basePath}/config/${coin}`,
        method: "GET"
      })
    }),

    getStarCourse: builder.query<IStarCourse, void>({
      query: () => ({
        url: `${basePath}/star-course`,
        method: "GET"
      })
    }),

    attachWallet: builder.mutation<IUser, IAttachWalletArgs>({
      query: (data) => ({
        url: `${basePath}/wallet/attach`,
        method: "POST",
        data
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          const { data: user } = await queryFulfilled
          patchUserWallets(dispatch, user.wallets)
        } catch {
          /* handled globally */
        }
      }
    }),

    disconnectWallet: builder.mutation<IUser, IDisconnectWalletArgs>({
      query: (data) => ({
        url: `${basePath}/wallet/disconnect`,
        method: "POST",
        data
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          const { data: user } = await queryFulfilled
          patchUserWallets(dispatch, user.wallets)
        } catch {
          /* handled globally */
        }
      }
    })
  })
})

export const {
  useSendVerificationMutation,
  useVerifyWithdrawalMutation,
  useCancelWithdrawalMutation,
  useGetWithdrawalHistoryQuery,
  useGetWithdrawalConfigQuery,
  useAttachWalletMutation,
  useDisconnectWalletMutation,
  useGetStarCourseQuery
} = withdrawalEndpoints
